<script setup lang="ts">
import type { Agegroup } from '~~/shared/ts/interfaces/Agegroup';
import type { IntroPopup } from '~~/shared/ts/interfaces/IntroPopup';
import type NavAgegroups from '~~/shared/ts/interfaces/NavAgegroups';
import type { StrapiData, StrapiDataCollection } from '~~/shared/ts/interfaces/strapi/StrapiData';

// Placeholder in the intro popup text (Strapi) that is replaced with the agegroup contacts
const AGEGROUPS_PLACEHOLDER = /\{\{\s*agegroups\s*\}\}/;

const open = defineModel<boolean>('open', { default: false });

const config = useRuntimeConfig();
const text = ref<string>('');
const agegroups = ref<Agegroup[]>([]);

// Text before and after the placeholder; without a placeholder everything is in `before`
const textParts = computed(() => {
    const [before = '', ...rest] = text.value.split(AGEGROUPS_PLACEHOLDER);
    return { before, after: rest.join(''), hasPlaceholder: rest.length > 0 };
});

function close() {
    open.value = false;
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') close();
}

// Lock the page scroll while the popup is open
watch(open, (value) => {
    document.documentElement.classList.toggle('is-clipped', value);
});

onMounted(async () => {
    window.addEventListener('keydown', onKeydown);
    const popupResponse = await fetch(`${config.public.apiUrl}/api/intro-popup`);
    text.value = ((await popupResponse.json()) as StrapiData<IntroPopup>).data?.text ?? '';

    const [response, navResponse] = await Promise.all([
        fetch(`${config.public.apiUrl}/api/agegroups?populate[0]=Contact&filters[showInIntroPopup][$eq]=true`),
        fetch(`${config.public.apiUrl}/api/agegroupnav?populate=agegroups`),
    ]);
    // the API rejects the filter until the field exists, the popup then shows without the contacts
    if (!response.ok) return;
    const result = ((await response.json()) as StrapiDataCollection<Agegroup>).data ?? [];

    // Same order as in the navbar, agegroups missing there go to the end
    const navOrder = navResponse.ok
        ? (((await navResponse.json()) as StrapiData<NavAgegroups>).data?.agegroups ?? []).map((a) => a.documentId)
        : [];
    const position = (agegroup: Agegroup) => {
        const index = navOrder.indexOf(agegroup.documentId);
        return index === -1 ? navOrder.length : index;
    };
    agegroups.value = result.sort((a, b) => position(a) - position(b));
});

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown);
    document.documentElement.classList.remove('is-clipped');
});
</script>

<template>
    <Teleport to="body">
        <Transition name="intro-popup">
            <div v-if="open" class="modal is-active intro-popup" role="dialog" aria-modal="true"
                aria-label="Kontakt">
                <div class="modal-background" @click="close"></div>
                <div class="modal-card intro-popup__card">
                    <section class="modal-card-body content">
                        <button class="delete is-large intro-popup__close" aria-label="Schliessen" @click="close"></button>
                        <UtilMarkdown v-if="textParts.before" :source="textParts.before" />

                        <template v-if="textParts.hasPlaceholder && agegroups.length">
                            <div class="intro-popup__contacts">
                                <div v-for="agegroup in agegroups" :key="agegroup.documentId" class="intro-popup__contact">
                                    <strong>{{ agegroup.longTitle }}</strong>
                                    <span v-if="agegroup.Contact?.Name">{{ agegroup.Contact.Name }}</span>
                                    <a v-if="agegroup.Contact?.Email" :href="`mailto:${agegroup.Contact.Email}`">
                                        {{ agegroup.Contact.Email }}</a>
                                    <a v-if="agegroup.Contact?.PhoneNumber" :href="`tel:${agegroup.Contact.PhoneNumber}`">
                                        {{ agegroup.Contact.PhoneNumber }}</a>
                                </div>
                            </div>
                        </template>

                        <UtilMarkdown v-if="textParts.after" :source="textParts.after" />
                    </section>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style lang="scss" scoped>
.intro-popup {
    &__card {
        width: 80vw;

        @media screen and (max-width: 1024px) {
            width: calc(100% - 40px);
        }
    }

    .modal-card-body {
        position: relative;
        border-radius: 6px;
        padding: 2rem 1.5rem;
    }

    // Wide markdown tables scroll horizontally instead of overflowing the card
    :deep(table) {
        display: block;
        overflow-x: auto;
    }

    &__close {
        position: absolute;
        top: 1rem;
        right: 1rem;
    }

    &__contacts {
        display: flex;
        gap: 1rem;
        margin-bottom: 1.5rem;

        @media screen and (max-width: 1024px) {
            flex-direction: column;
        }
    }

    &__contact {
        flex: 1 1 0;
        min-width: 0;
        display: flex;
        flex-direction: column;
        padding: 0.75rem;
        border-left: 4px solid $primary;
        background-color: #f7f7f7;
        border-radius: 4px;
        overflow-wrap: anywhere;
    }
}

.intro-popup-enter-active,
.intro-popup-leave-active {
    transition: opacity 0.15s ease;
}

.intro-popup-enter-from,
.intro-popup-leave-to {
    opacity: 0;
}
</style>
